import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Field, change } from 'redux-form';
import { useSelector, useDispatch } from 'react-redux';

import { withTheme } from 'common/styling/theme';
import { LightBoxModal, RadioButton, SimpleModal } from 'common/components';
import { FileField } from 'common/components/form-fields';
import iconDocument from 'common/images/preview2.png';

import classNames from 'classnames';
import { getUserDocumentFile, deletePOIDocuments } from '../../api';
import { getUserDocuments, deleteUserDocument } from '../../_redux';
import { staticStyles, getDynamicStyles } from './style';

/**
 * 已上传文件列表：预览 + 状态角标 + 删除。
 * 图片用 LightBox 预览，PDF 浏览器原生渲染；docx 用 docx-preview 新标签页渲染；
 * .doc 等其他格式浏览器无法预览，只能打开/下载。
 * verified 文件在缩略图右上角显示绿色对勾角标；deletable 时 unverified 文件显示删除按钮。
 * 四个文档类型（非 POI）面板已 verified 时由父级传 deletable=false，锁定整面板（不显示任何删除按钮）；
 * 未 verified 的面板 deletable=true，保留 unverified 文件的删除按钮。verifiedFirst 开启时，通过审核的文件排在前面。
 */
const UploadedFiles = ({
  files = [],
  name,
  side = null,
  poiDocType = null,
  blobs = {},
  onLoadFile = () => {},
  deletable = true,
  verifiedFirst = false,
}) => {
  const dispatch = useDispatch();
  const [lightBoxIndex, setLightBoxIndex] = useState(-1);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // 按 side / poiDocType 过滤（identification_proof 双面用；非 POI 不传过滤条件）。
  // 注意：本项目 monkeypatch 了 DRF 的 Serializer.to_representation，序列化输出字段名
  // 一律转成 camelCase，所以响应里证件类型字段名是 poiDocType（不是模型上的 poi_doc_type）。
  // 之前读 f.poi_doc_type 永远取不到值，所有文件被默认归到 passport，导致全部显示在 passport 下。
  const filtered = files.filter(f => {
    // rejected（被拒）的文件是旧提交，用户已重新上传新的，直接忽略不展示
    if (f.status === 'rejected') return false;
    if (side && f.side !== side) return false;
    // 兼容历史数据：多证件类型功能上线前上传的 identification_proof 没有 poiDocType，
    // 一律按默认类型 passport 展示，否则这些文件永远匹配不上过滤条件而不显示。
    const filePoiType = f.poiDocType || f.poi_doc_type || 'passport';
    if (poiDocType && filePoiType !== poiDocType) return false;
    return true;
  });

  // 通过审核的文件排到最前面（四个文档类型开启 verifiedFirst；filtered 已排除 rejected，
  // 剩余只有 unverified / verified，稳定排序保持同状态文件原顺序）
  const visibleFiles = verifiedFirst
    ? [...filtered].sort((a, b) => Number(b.status === 'verified') - Number(a.status === 'verified'))
    : filtered;

  const filteredIds = visibleFiles.map(f => f.id).join(',');

  // 触发父级加载当前可见文件的 blob。缓存由父级 DocumentsLoadPanel 统一持有，
  // identification_proof 切换证件类型时本组件会因单面/双面布局差异重新挂载，
  // 但父级缓存不丢，来回切换 passport 不会再重复下载。
  useEffect(() => {
    visibleFiles.forEach(f => onLoadFile(f.id));
  }, [filteredIds, onLoadFile]);

  if (visibleFiles.length === 0) return null;

  // 图片文件的 objectURL 数组（供 LightBox 图集）
  const images = [];
  const imageIndexByFileId = {};
  visibleFiles.forEach(f => {
    const b = blobs[f.id];
    if (b && b.mime && b.mime.startsWith('image/')) {
      imageIndexByFileId[f.id] = images.length;
      images.push(b.url);
    }
  });

  return (
    <div className="DocumentsLoadPanel__files">
      {visibleFiles.map(f => {
        const b = blobs[f.id];
        const isImage = !!(b && b.mime && b.mime.startsWith('image/'));

        let preview;
        if (isImage) {
          // 缩略图：直接用 <button><img object-fit: contain>，避免跨组件 :global 样式不生效导致原尺寸展示
          preview = (
            <FormattedMessage id="clientPreviewDocument">
              {txt => (
                <button
                  type="button"
                  className="DocumentsLoadPanel__file-thumb"
                  title={txt}
                  aria-label={txt}
                  onClick={() => setLightBoxIndex(imageIndexByFileId[f.id])}>
                  <img src={b.url} alt={txt} />
                </button>
              )}
            </FormattedMessage>
          );
        } else if (b) {
          // PDF 浏览器原生渲染；.doc 等其他格式无法预览（触发下载）
          preview = (
            <a className="DocumentsLoadPanel__file-nonimage" href={b.url} target="_blank" rel="noopener noreferrer">
              <img src={iconDocument} alt="" />
            </a>
          );
        } else {
          // blob 尚未加载成功：仅显示文件图标
          preview = (
            <span className="DocumentsLoadPanel__file-nonimage">
              <img src={iconDocument} alt="" />
            </span>
          );
        }

        return (
          <div className="DocumentsLoadPanel__file" key={f.id}>
            <div className="DocumentsLoadPanel__file-media">{preview}</div>

            {/* 已审核通过：缩略图右上角绿色对勾角标（替换删除按钮位置，verified 不显示删除） */}
            {f.status === 'verified' && (
              <FormattedMessage id="status_verified">
                {txt => (
                  <span className="DocumentsLoadPanel__file-verified" title={txt} aria-label={txt}>
                    <i />
                  </span>
                )}
              </FormattedMessage>
            )}

            {/* deletable 为 false（四个文档类型）时隐藏所有删除按钮；为 true（POI）时仅 unverified 可删 */}
            {deletable && f.status === 'unverified' && (
              <FormattedMessage id="clientDeleteDocument">
                {txt => (
                  <button
                    type="button"
                    className="DocumentsLoadPanel__file-delete"
                    title={txt}
                    onClick={() => setConfirmDeleteId(f.id)}>
                    <span aria-hidden="true">×</span>
                  </button>
                )}
              </FormattedMessage>
            )}
          </div>
        );
      })}

      {images.length > 0 && (
        <LightBoxModal currentImage={lightBoxIndex} images={images} onRequestClose={() => setLightBoxIndex(-1)} />
      )}

      {confirmDeleteId !== null && (
        <SimpleModal
          caption={{ id: 'clientDeleteDocumentConfirm' }}
          content={{ id: 'clientDeleteDocumentConfirmText' }}
          closeHandler={() => setConfirmDeleteId(null)}
          actionButton={{
            textId: 'justConfirm',
            handler: () => dispatch(deleteUserDocument.request({ id: confirmDeleteId, name })),
          }}
          cancelButton={{ textId: 'justCancel' }}
        />
      )}

      {/* UploadedFiles 是独立函数组件：必须自行声明 <style jsx> 才能让作用域样式
          （缩略图尺寸、object-fit: contain 等）落到自己渲染的元素上。
          同文件同哈希，styled-jsx 按 id 去重，不会与 DocumentsLoadPanel 重复注入。 */}
      <style jsx>{staticStyles}</style>
    </div>
  );
};

UploadedFiles.propTypes = {
  files: PropTypes.array,
  name: PropTypes.string.isRequired,
  side: PropTypes.string,
  poiDocType: PropTypes.string,
  blobs: PropTypes.object,
  onLoadFile: PropTypes.func,
  deletable: PropTypes.bool,
  verifiedFirst: PropTypes.bool,
};

UploadedFiles.defaultProps = {
  files: [],
  side: null,
  poiDocType: null,
  blobs: {},
  onLoadFile: () => {},
  deletable: true,
  verifiedFirst: false,
};

/**
 * 返回 identification_proof 默认应选中的证件类型（passport → national_id → driving_license）。
 * 优先选中"有在审/已通过文件"的类型（unverified/verified）：rejected 文件对用户来说等于没上传成功，
 * 不应抢走默认选择（例：旧 passport 被拒、后来上传了 national_id 正面，应默认选中 national_id）。
 * 没有任何在审/已通过文件时才退回第一个有文件记录的类型（含 rejected）；无文件返回 null。
 * 用于未 verified 时进入页面，默认把单选按钮选中到合适的选项，而不是固定的 passport。
 */
const getFirstUploadedPoiType = summary => {
  if (!summary) return null;
  const hasActiveFile = (front, back) =>
    front === 'unverified' || front === 'verified' || back === 'unverified' || back === 'verified';

  // 优先选中"有在审/已通过文件"的类型
  if (hasActiveFile(summary.passport, null)) return 'passport';
  if (hasActiveFile(summary.national_id_front, summary.national_id_back)) return 'national_id';
  if (hasActiveFile(summary.driving_license_front, summary.driving_license_back)) return 'driving_license';

  // 无在审/已通过文件时，退回第一个有文件记录的类型（含 rejected）
  const hasAnyFile = (front, back) =>
    (front != null && front !== 'notUploaded') || (back != null && back !== 'notUploaded');
  if (hasAnyFile(summary.passport, null)) return 'passport';
  if (hasAnyFile(summary.national_id_front, summary.national_id_back)) return 'national_id';
  if (hasAnyFile(summary.driving_license_front, summary.driving_license_back)) return 'driving_license';
  return null;
};

const DocumentsLoadPanel = ({
  captionId,
  descriptionId,
  status,
  name,
  onClickAction,
  reupload,
  uploadError,
  profileStatus,
  fileNameId,
  mandatory,
  onUplaod,
  docLimit,
  uploadedDocs,
  identificationProofSummary,
  isOldPoiType,
  theme,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const [moreLimit, setMoreLimit] = useState(false);
  const [showReuploadConfirm, setShowReuploadConfirm] = useState(false);

  // profile 也是 DRF camelCase 序列化，证件类型字段名是 poiDocType（兜底兼容 snake_case）
  const serverPoiDocType = useSelector(state => state.user?.profile?.poiDocType ?? state.user?.profile?.poi_doc_type);

  const dispatch = useDispatch();
  const documentsList = useSelector(state => state.client.kyc?.documents?.[name] || []);

  // 已上传文件的 blob 缓存放在父级：identification_proof 切换证件类型时，
  // 单面/双面布局结构变化会重新挂载 UploadedFiles，缓存放父级保证不重复下载。
  const [sharedBlobs, setSharedBlobs] = useState({}); // { [id]: { url, mime } }
  const fetchedIdsRef = useRef({});
  const sharedUrlsRef = useRef({});
  const loadBlob = useCallback(id => {
    if (fetchedIdsRef.current[id]) return;
    fetchedIdsRef.current[id] = true;
    getUserDocumentFile(id)
      .then(res => {
        const url = URL.createObjectURL(res.data);
        sharedUrlsRef.current[id] = url;
        setSharedBlobs(prev => ({ ...prev, [id]: { url, mime: res.data.type } }));
      })
      .catch(() => {
        /* 拉取失败：不展示该文件 */
      });
  }, []);

  // 面板卸载时 revoke 所有 objectURL，避免内存泄漏
  useEffect(
    () => () => {
      Object.values(sharedUrlsRef.current).forEach(url => URL.revokeObjectURL(url));
      sharedUrlsRef.current = {};
    },
    []
  );

  // 组件挂载时拉取该类型已上传的文件列表（上传/删除后由 saga 刷新）。
  // 额外依赖状态签名：WebSocket user_update（实时刷新）只推送 profile 状态字段，
  // 从不重拉文件列表 —— reject/verify 实时到达后，列表里旧文件的 status 仍是
  // unverified（缩略图+删除按钮继续显示），与 F5（重挂载重拉）的结果不一致。
  // 状态变化时重拉列表，保证实时刷新与 F5 一致。status 对 POI 可能是 [front, back]
  // 数组，用 JSON.stringify 做稳定签名，避免数组引用随每次 profile 更新变化导致无谓重拉。
  const statusSig = JSON.stringify(status);
  // 多文件证件类型：reject 其中一份后 get_document_status 聚合仍可能返回
  // unverified（for_all('rejected') 才 rejected），仅依赖 status 不会触发重拉，
  // 被拒文件的缩略图就留住了。uploaded_documents_count 排除 rejected —— 拒绝后该
  // 类型计数下降（后端 on_commit 推送保证载荷是提交后的），用它兜底触发重拉。
  const uploadedDocsSig = JSON.stringify(uploadedDocs);
  const poSummarySig = name === 'identification_proof' ? JSON.stringify(identificationProofSummary) : null;
  useEffect(() => {
    dispatch(getUserDocuments.request(name));
  }, [name, statusSig, uploadedDocsSig, poSummarySig]);

  // docType state with persistence — MUST be before status computations
  // 未 verified 时优先默认选中"第一个有上传文件的证件类型"（identificationProofSummary 就绪的情况下）；
  // summary 可能异步到达，useEffect（下方）会做一次补偿同步。
  const [docType, setDocType] = useState(() => {
    if (name !== 'identification_proof') return 'passport';
    return (
      getFirstUploadedPoiType(identificationProofSummary) ||
      serverPoiDocType ||
      localStorage.getItem('poi_doc_type') ||
      'passport'
    );
  });

  // Derive per-type/side status. For identification_proof, use summary; otherwise use status prop.
  const summary = identificationProofSummary || {};
  const getProofStatus = (type, side = null) => {
    if (type === 'passport') return summary.passport || 'notUploaded';
    if (type === 'national_id')
      return side === 'back' ? summary.national_id_back || 'notUploaded' : summary.national_id_front || 'notUploaded';
    if (type === 'driving_license')
      return side === 'back'
        ? summary.driving_license_back || 'notUploaded'
        : summary.driving_license_front || 'notUploaded';
    return 'notUploaded';
  };

  const isPOI = name === 'identification_proof';

  // 旧版无类型 POI（isOldPoiType=1）+ 用户整体已 verified：不展示单选/文件，仅显示 Verified
  const oldPoiVerified = isPOI && isOldPoiType === 1 && profileStatus === 'verified';

  // When a POI doc type is fully verified, lock the selection to the first verified type
  // (in order: passport → national_id → driving_license) and gray out the radio buttons.
  const poiVerifiedType = isPOI
    ? summary.passport === 'verified'
      ? 'passport'
      : summary.national_id_front === 'verified' && summary.national_id_back === 'verified'
      ? 'national_id'
      : summary.driving_license_front === 'verified' && summary.driving_license_back === 'verified'
      ? 'driving_license'
      : null
    : null;
  const poiLocked = poiVerifiedType !== null;

  // When locked, render as the verified type immediately (avoids flashing the wrong type before the effect syncs)
  // oldPoiVerified 时强制为空，使三个单选都不选中（只显示 Verified）
  const selectedDocType = oldPoiVerified ? '' : poiLocked ? poiVerifiedType : docType;

  const frontStatus = isPOI ? getProofStatus(selectedDocType, 'front') : Array.isArray(status) ? status[0] : status;
  const backStatus = isPOI ? getProofStatus(selectedDocType, 'back') : Array.isArray(status) ? status[1] : status;
  const effectiveStatus = isPOI
    ? selectedDocType === 'passport'
      ? getProofStatus('passport')
      : frontStatus === 'verified' && backStatus === 'verified'
      ? 'verified'
      : frontStatus === 'unverified' || backStatus === 'unverified'
      ? 'unverified'
      : frontStatus === 'rejected' || backStatus === 'rejected'
      ? 'rejected'
      : 'notUploaded'
    : Array.isArray(status)
    ? status.every(s => s === 'verified')
      ? 'verified'
      : status.some(s => s === 'unverified')
      ? 'unverified'
      : 'notUploaded'
    : status;

  // 四个文档类型（非 POI）：只有面板已 verified 才锁定文件（隐藏删除按钮、绿色角标替换）；
  // 未 verified 的面板保留 unverified 文件的删除按钮，方便用户管理待审核文件。
  // identification_proof（POI）始终可删（其文件级 unverified 判断在 UploadedFiles 内生效）。
  // 旧版无类型 POI + 已 verified：文件只读展示（无删除按钮）。
  const deletable = !oldPoiVerified && (isPOI || effectiveStatus !== 'verified');

  const allowedToUploadDocumentsStatuses = ['notUploaded', 'rejected', 'enabled', 'unverificated'];

  const acceptedFormats = [
    'image/png',
    'image/jpeg',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  // Track upload state for front/back sides in the current session
  const [frontUploaded, setFrontUploaded] = useState(false);
  const [backUploaded, setBackUploaded] = useState(false);

  // Local re-upload state to allow individual re-uploading
  const [localReuploadFront, setLocalReuploadFront] = useState(false);
  const [localReuploadBack, setLocalReuploadBack] = useState(false);

  // Track which docType was uploaded to avoid showing false uploaded state when switching
  const [uploadedDocType, setUploadedDocType] = useState(() => {
    if (name !== 'identification_proof') return null;
    return (
      serverPoiDocType ||
      localStorage.getItem('poi_uploaded_doc_type') ||
      localStorage.getItem('poi_doc_type') ||
      'passport'
    );
  });

  // Sync docType if server value changes
  // 未 verified 且已有上传文件时，不让 profile 持久化的旧类型覆盖"第一个有文件"的默认选择
  // （否则 serverPoiDocType 固定在 passport，用户上传过 national_id 后刷新会跳回 passport）。
  useEffect(() => {
    if (name === 'identification_proof' && serverPoiDocType && serverPoiDocType !== docType) {
      if (poiLocked || !getFirstUploadedPoiType(identificationProofSummary)) {
        setDocType(serverPoiDocType);
        setUploadedDocType(serverPoiDocType);
      }
    }
  }, [serverPoiDocType, name, poiLocked, identificationProofSummary, docType]);

  // 未 verified 时，summary 就绪后把默认证件类型同步为"第一个有上传文件的选项"
  // （useState 初始化已尝试，但 summary 可能异步到达，这里补偿一次；之后不再干预用户手动切换）。
  const didAutoSelectDocTypeRef = useRef(false);
  useEffect(() => {
    if (!isPOI || poiLocked) return;
    const firstUploaded = getFirstUploadedPoiType(identificationProofSummary);
    if (!firstUploaded || didAutoSelectDocTypeRef.current) return;
    didAutoSelectDocTypeRef.current = true;
    if (docType !== firstUploaded) {
      setDocType(firstUploaded);
      setUploadedDocType(firstUploaded);
    }
  }, [identificationProofSummary, isPOI, poiLocked, docType]);

  // Lock docType to the first fully-verified POI type (radio buttons are disabled in this case)
  useEffect(() => {
    if (isPOI && poiVerifiedType && poiVerifiedType !== docType) {
      setDocType(poiVerifiedType);
      setUploadedDocType(poiVerifiedType);
    }
  }, [isPOI, poiVerifiedType, docType]);

  // Reset local state when global reupload is toggled on
  useEffect(() => {
    if (reupload) {
      setFrontUploaded(false);
      setBackUploaded(false);
      setLocalReuploadFront(false);
      setLocalReuploadBack(false);
    }
  }, [reupload]);

  const handleDocTypeChange = type => {
    setDocType(type);
    try {
      localStorage.setItem('poi_doc_type', type);
    } catch (e) {
      /* ignore */
    }
    // updatePOIDocType(type);
    setFrontUploaded(false);
    setBackUploaded(false);
    setLocalReuploadFront(false);
    setLocalReuploadBack(false);
  };

  const handleUpload = files => {
    if (docLimit && files.length > docLimit - uploadedDocs) {
      setMoreLimit(true);
    } else {
      setMoreLimit(false);
      if (name === 'identification_proof') {
        setUploadedDocType(docType);
        try {
          localStorage.setItem('poi_uploaded_doc_type', docType);
        } catch (e) {
          /* ignore */
        }
      }
      onUplaod({ name, files, ...(name === 'identification_proof' && { poiDocType: docType }) });
      // 清空 FileField 的 redux-form 字段值，避免删除最后一个文件后重挂载时
      // 残留的 File 对象被恢复、缩略图盖住 "drop your xxxx here" 提示。
      dispatch(change('DocumentsForm', name, []));
    }
  };

  const handleFrontUpload = files => {
    if (docLimit && files.length > docLimit - uploadedDocs) {
      setMoreLimit(true);
    } else {
      setMoreLimit(false);
      setLocalReuploadFront(false);
      if (name === 'identification_proof') {
        setUploadedDocType(docType);
        try {
          localStorage.setItem('poi_uploaded_doc_type', docType);
        } catch (e) {
          /* ignore */
        }
      }
      onUplaod({ name, files, side: 'front', ...(name === 'identification_proof' && { poiDocType: docType }) });
      dispatch(change('DocumentsForm', `${name}_front`, []));
    }
  };

  const handleBackUpload = files => {
    if (docLimit && files.length > docLimit - uploadedDocs) {
      setMoreLimit(true);
    } else {
      setMoreLimit(false);
      setLocalReuploadBack(false);
      if (name === 'identification_proof') {
        setUploadedDocType(docType);
        try {
          localStorage.setItem('poi_uploaded_doc_type', docType);
        } catch (e) {
          /* ignore */
        }
      }
      onUplaod({ name, files, side: 'back', ...(name === 'identification_proof' && { poiDocType: docType }) });
      dispatch(change('DocumentsForm', `${name}_back`, []));
    }
  };

  const handleReuploadFront = () => {
    setLocalReuploadFront(true);
    setFrontUploaded(false);
  };

  const handleReuploadBack = () => {
    setLocalReuploadBack(true);
    setBackUploaded(false);
  };

  const handleReuploadAll = async () => {
    setShowReuploadConfirm(false);
    setDocType('passport');
    setFrontUploaded(false);
    setBackUploaded(false);
    setLocalReuploadFront(false);
    setLocalReuploadBack(false);
    setUploadedDocType(null);
    try {
      localStorage.removeItem('poi_doc_type');
      localStorage.removeItem('poi_uploaded_doc_type');
    } catch (e) {
      /* ignore */
    }
    try {
      await deletePOIDocuments();
    } catch (e) {
      /* ignore */
    }
  };

  const captionIdNew =
    captionId === 'proofOfLife' &&
    (document.location.hostname === 'my.tixee.com' || document.location.hostname === 'in.tredero.com')
      ? 'proofOfLifeTixee'
      : captionId;

  const needsTwoSides = name === 'identification_proof' && selectedDocType !== 'passport';
  // isDocTypeMismatch not needed with identificationProofSummary — it forces dropzone incorrectly
  const isDocTypeMismatch = isPOI
    ? false
    : name === 'identification_proof' && docType !== uploadedDocType && status === 'unverified';

  const showFrontDropzone =
    !frontUploaded &&
    (reupload ||
      localReuploadFront ||
      allowedToUploadDocumentsStatuses.includes(frontStatus) ||
      backUploaded ||
      isDocTypeMismatch);
  const showBackDropzone =
    !backUploaded &&
    (reupload ||
      localReuploadBack ||
      allowedToUploadDocumentsStatuses.includes(backStatus) ||
      frontUploaded ||
      isDocTypeMismatch);

  return (
    <div className="DocumentsLoadPanel" id={`client-document-${name}`}>
      <div className="DocumentsLoadPanel__caption">
        <FormattedMessage id={captionIdNew}>{txt => <h2>{txt}</h2>}</FormattedMessage>

        <FormattedMessage id={descriptionId}>{txt => <p>{txt}</p>}</FormattedMessage>

        {!mandatory && ['unverified', 'incomplete'].includes(profileStatus) && (
          <FormattedMessage id="kycMandatoryHelpText">{txt => <p>{txt}</p>}</FormattedMessage>
        )}
      </div>

      {/* 四个文档类型（非 POI）：正文描述下方显示 green verified 指示，状态来自 api/user/ 的 *_Status 字段 */}
      {!isPOI && effectiveStatus === 'verified' && (
        <FormattedMessage id="status_verified">
          {txt => (
            <span className="Document__status-verified">
              {txt}
              <i />
            </span>
          )}
        </FormattedMessage>
      )}

      {/* Radio buttons + Reupload button in one row */}
      {name === 'identification_proof' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20 }}>
          <div className="DocumentsLoadPanel__doc-types" style={{ display: 'flex', gap: 20, flex: 1 }}>
            <RadioButton
              name="docType"
              value="passport"
              checked={selectedDocType === 'passport'}
              onChange={() => handleDocTypeChange('passport')}
              textId="idTypePassport"
              disabled={poiLocked || oldPoiVerified}
            />
            <RadioButton
              name="docType"
              value="national_id"
              checked={selectedDocType === 'national_id'}
              onChange={() => handleDocTypeChange('national_id')}
              textId="idTypeNationalID"
              disabled={poiLocked || oldPoiVerified}
            />
            <RadioButton
              name="docType"
              value="driving_license"
              checked={selectedDocType === 'driving_license'}
              onChange={() => handleDocTypeChange('driving_license')}
              textId="idTypeDrivingLicense"
              disabled={poiLocked || oldPoiVerified}
            />
          </div>
          {false && (
            <button
              type="button"
              onClick={() => setShowReuploadConfirm(true)}
              style={{
                background: 'none',
                border: '1px solid #d32f2f',
                color: '#d32f2f',
                padding: '6px 16px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 13,
                whiteSpace: 'nowrap',
              }}>
              <FormattedMessage id="poiReupload" />
            </button>
          )}
          {false && showReuploadConfirm && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}>
              <button
                type="button"
                aria-label="Close dialog"
                onClick={() => setShowReuploadConfirm(false)}
                style={{
                  position: 'absolute',
                  inset: 0,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'default',
                }}
              />
              <div
                role="dialog"
                aria-modal="true"
                style={{
                  position: 'relative',
                  background: '#fff',
                  padding: 30,
                  borderRadius: 12,
                  maxWidth: 450,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                }}>
                <p style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
                  <FormattedMessage id="poiReuploadConfirm" />
                </p>
                <div style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => setShowReuploadConfirm(false)}
                    style={{
                      background: '#eee',
                      border: 'none',
                      padding: '8px 20px',
                      borderRadius: 4,
                      marginRight: 10,
                      cursor: 'pointer',
                    }}>
                    <FormattedMessage id="justCancel" />
                  </button>
                  <button
                    type="button"
                    onClick={handleReuploadAll}
                    style={{
                      background: '#d32f2f',
                      color: '#fff',
                      border: 'none',
                      padding: '8px 20px',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}>
                    <FormattedMessage id="justConfirm" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 旧版无类型 POI + 用户已 verified：不展示上传区/文件列表，只显示 Verified 图标 */}
      {oldPoiVerified ? (
        <FormattedMessage id="status_verified">
          {txt => (
            <p className="Document__status-verified">
              {txt}
              <i />
            </p>
          )}
        </FormattedMessage>
      ) : needsTwoSides ? (
        <div className="DocumentsLoadPanel__sides-row">
          {/* Front Side */}
          <div className="DocumentsLoadPanel__side-section">
            <div className="DocumentsLoadPanel__side-label">
              <FormattedMessage id="frontSideOfDocument" />
            </div>
            <div
              className={classNames('DocumentsLoadPanel__input-inner', {
                'DocumentsLoadPanel__input-inner--error': moreLimit && showFrontDropzone,
              })}>
              {showFrontDropzone ? (
                <Field
                  component={FileField}
                  handleAfterUpload={handleFrontUpload}
                  name={`${name}_front`}
                  formatNameId="documentsFormats"
                  fileNameId={fileNameId}
                  accept={acceptedFormats.join(',')}
                  multiple
                />
              ) : (
                <>
                  {frontStatus === 'unverified' && profileStatus !== 'verified' && (
                    <div className="DocumentsForm__upload">
                      <div className="DocumentsForm__upload-text">
                        <i />
                        <FormattedMessage id="clientDocumentUpload">{txt => <p>{txt}</p>}</FormattedMessage>
                        <FormattedMessage id="clientDocumentUploadDescription" />
                      </div>

                      {(!docLimit || (docLimit && docLimit > uploadedDocs)) && (
                        <div className="DocumentsForm__upload-button">
                          <FormattedMessage id="clientDocumentUploadAnother">
                            {txt => (
                              <button type="button" onClick={handleReuploadFront}>
                                {txt}
                              </button>
                            )}
                          </FormattedMessage>
                        </div>
                      )}
                    </div>
                  )}
                  {frontStatus === 'verified' && (
                    <FormattedMessage id="status_verified">
                      {txt => (
                        <p className="Document__status-verified">
                          {txt}
                          <i />
                        </p>
                      )}
                    </FormattedMessage>
                  )}
                </>
              )}
            </div>
            <UploadedFiles
              files={documentsList}
              name={name}
              side="front"
              poiDocType={isPOI ? selectedDocType : null}
              blobs={sharedBlobs}
              onLoadFile={loadBlob}
            />
          </div>

          {/* Back Side */}
          <div className="DocumentsLoadPanel__side-section">
            <div className="DocumentsLoadPanel__side-label">
              <FormattedMessage id="backSideOfDocument" />
            </div>
            <div
              className={classNames('DocumentsLoadPanel__input-inner', {
                'DocumentsLoadPanel__input-inner--error': moreLimit && showBackDropzone,
              })}>
              {showBackDropzone ? (
                <Field
                  component={FileField}
                  handleAfterUpload={handleBackUpload}
                  name={`${name}_back`}
                  formatNameId="documentsFormats"
                  fileNameId={fileNameId}
                  accept={acceptedFormats.join(',')}
                  multiple
                />
              ) : (
                <>
                  {backStatus === 'unverified' && profileStatus !== 'verified' && (
                    <div className="DocumentsForm__upload">
                      <div className="DocumentsForm__upload-text">
                        <i />
                        <FormattedMessage id="clientDocumentUpload">{txt => <p>{txt}</p>}</FormattedMessage>
                        <FormattedMessage id="clientDocumentUploadDescription" />
                      </div>

                      {(!docLimit || (docLimit && docLimit > uploadedDocs)) && (
                        <div className="DocumentsForm__upload-button">
                          <FormattedMessage id="clientDocumentUploadAnother">
                            {txt => (
                              <button type="button" onClick={handleReuploadBack}>
                                {txt}
                              </button>
                            )}
                          </FormattedMessage>
                        </div>
                      )}
                    </div>
                  )}
                  {backStatus === 'verified' && (
                    <FormattedMessage id="status_verified">
                      {txt => (
                        <p className="Document__status-verified">
                          {txt}
                          <i />
                        </p>
                      )}
                    </FormattedMessage>
                  )}
                </>
              )}
            </div>
            <UploadedFiles
              files={documentsList}
              name={name}
              side="back"
              poiDocType={isPOI ? selectedDocType : null}
              blobs={sharedBlobs}
              onLoadFile={loadBlob}
            />
          </div>
        </div>
      ) : (
        /* Standard single upload area (passport or other document types) */
        <div
          className={classNames('DocumentsLoadPanel__input-inner', {
            'DocumentsLoadPanel__input-inner--error': moreLimit,
          })}>
          {(allowedToUploadDocumentsStatuses.includes(frontStatus) || reupload || isDocTypeMismatch) && (
            <Field
              component={FileField}
              handleAfterUpload={handleUpload}
              name={name}
              formatNameId="documentsFormats"
              fileNameId={fileNameId}
              accept={acceptedFormats.join(',')}
              multiple
            />
          )}

          {frontStatus === 'unverified' && !reupload && profileStatus !== 'verified' && !isDocTypeMismatch && (
            <div className="DocumentsForm__upload">
              <div className="DocumentsForm__upload-text">
                <i />
                <FormattedMessage id="clientDocumentUpload">{txt => <p>{txt}</p>}</FormattedMessage>
                <FormattedMessage id="clientDocumentUploadDescription" />
              </div>

              {!docLimit && (
                <div className="DocumentsForm__upload-button">
                  <FormattedMessage id="clientDocumentUploadAnother">
                    {txt => (
                      <button type="button" onClick={() => onClickAction(name)}>
                        {txt}
                      </button>
                    )}
                  </FormattedMessage>
                </div>
              )}

              {docLimit && docLimit > uploadedDocs && (
                <div className="DocumentsForm__upload-button">
                  <FormattedMessage id="clientDocumentUploadAnother">
                    {txt => (
                      <button type="button" onClick={() => onClickAction(name)}>
                        {txt}
                      </button>
                    )}
                  </FormattedMessage>
                </div>
              )}
            </div>
          )}

          {name === 'identification_proof' && effectiveStatus === 'verified' && (
            <FormattedMessage id="status_verified">
              {txt => (
                <p className="Document__status-verified">
                  {txt}
                  <i />
                </p>
              )}
            </FormattedMessage>
          )}
        </div>
      )}

      {/* 单面模式：文件列表放在整个输入区下方。
          四个文档类型（非 POI）：通过审核的排最前（verifiedFirst=true）；仅当面板已 verified 时隐藏删除按钮
          （deletable=false，绿色角标锁定），未 verified 的面板保留 unverified 文件的删除按钮。
          identification_proof 的 passport 单面沿用该位置，保持原逻辑：unverified 可删（deletable=true）、不排序。
          旧版无类型 POI（oldPoiVerified）：selectedDocType 为空串使 needsTwoSides 为 true，但仍需渲染单文件列表，
          故用 oldPoiVerified 强制放行（deletable=false 只读展示）。 */}
      {(!needsTwoSides || oldPoiVerified) && (
        <UploadedFiles
          files={documentsList}
          name={name}
          poiDocType={isPOI ? selectedDocType : null}
          blobs={sharedBlobs}
          onLoadFile={loadBlob}
          deletable={deletable}
          verifiedFirst={!isPOI}
        />
      )}

      {uploadError && <span className="DocumentsLoadPanel__error">{uploadError}</span>}
      {moreLimit && (
        <span className="DocumentsLoadPanel__error">
          <FormattedMessage id="clientMaxDocError" />
        </span>
      )}

      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

DocumentsLoadPanel.propTypes = {
  captionId: PropTypes.string.isRequired,
  descriptionId: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onClickAction: PropTypes.func.isRequired,
  reupload: PropTypes.bool,
  uploadError: PropTypes.string,
  profileStatus: PropTypes.string.isRequired,
  fileNameId: PropTypes.string.isRequired,
  mandatory: PropTypes.bool.isRequired,
  onUplaod: PropTypes.func.isRequired,
  docLimit: PropTypes.number,
  uploadedDocs: PropTypes.number,
  identificationProofSummary: PropTypes.object,
  isOldPoiType: PropTypes.number,
  theme: PropTypes.object,
};

DocumentsLoadPanel.defaultProps = {
  reupload: false,
  uploadError: '',
  docLimit: 0,
  uploadedDocs: 0,
  identificationProofSummary: {},
  isOldPoiType: 0,
  theme: {},
};

const ThemedDocumentsLoadPanel = withTheme()(DocumentsLoadPanel);

export default ThemedDocumentsLoadPanel;
export { DocumentsLoadPanel, UploadedFiles };
